/**
 * Campanita del centro de notificaciones (ADR 0022 — fase 4).
 *
 * Se monta en dos lugares (`App.tsx` para el dashboard del tenant,
 * `AdminShell.tsx` para la consola de plataforma) porque los productores de
 * hoy notifican a AMBOS planos — el único productor real mergeado
 * (`email-failure-inbox.ts`) le habla a `superAdmin`, que vive enteramente
 * en `/admin`. Un solo montaje en el dashboard tenant lo dejaría sin ver sus
 * propias notificaciones.
 *
 * Toda la lógica (fetch inicial, WS, backoff, catch-up, dedupe) vive en
 * `useNotificationCenter` — este archivo es presentación + el cableado de
 * UI que el hook deliberadamente no conoce (toasts, navegación al click).
 *
 * Toggle "Ver ocultas" (TODO.md §Centro de notificaciones): el panel alterna
 * entre la bandeja normal y la vista de ocultas vía `mode`/`setMode` del
 * hook (`GET /api/notifications?hidden=only`). En modo "Ocultas" la acción
 * de la fila deja de ser "Ocultar" y pasa a ser "Restaurar" (`unhide`) — no
 * tiene sentido volver a ocultar algo que ya está oculto. Ocultar SIGUE
 * mostrando además el toast "Deshacer" de siempre (atajo inmediato sin tener
 * que abrir el toggle); las dos vías llaman al mismo `unhide`, no son
 * caminos distintos.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import {
  describeConnectionStatus,
  formatRelativeTime,
  resolveNotificationTarget,
} from "../../lib/notifications/notification-policy";
import type { NotificationDTO, NotificationViewMode } from "../../lib/notifications/types";
import { useNotificationCenter } from "../../lib/notifications/useNotificationCenter";
import "./notifications.css";

const UNREAD_BADGE_CAP = 9;

/** Texto del badge numérico — capado en "9+" para no romper el layout de la campanita. */
export function formatUnreadBadge(count: number): string {
  return count > UNREAD_BADGE_CAP ? `${UNREAD_BADGE_CAP}+` : String(count);
}

/**
 * `aria-label` del botón trigger. El conteo no puede depender solo del
 * badge visual (color/tamaño) — un lector de pantalla necesita el número
 * en texto, no en un `<span>` decorativo.
 */
export function describeBellAriaLabel(unreadCount: number): string {
  if (unreadCount === 0) return "Notificaciones, sin novedades";
  if (unreadCount === 1) return "Notificaciones, 1 sin leer";
  return `Notificaciones, ${unreadCount} sin leer`;
}

/**
 * Texto del toggle "Ver ocultas" — cambia con el modo activo (no solo el
 * estilo `--active`, ver `notifications.css`), así que el estado se percibe
 * sin depender del color. `aria-pressed` (ver JSX) es la señal para lectores
 * de pantalla; el texto es la señal para el resto.
 */
export function describeToggleLabel(mode: NotificationViewMode): string {
  return mode === "hidden" ? "Ver recientes" : "Ver ocultas";
}

/** Copy del estado vacío — distinto por vista: en "Ocultas" no es un error, es lo esperable. */
export function describeEmptyState(mode: NotificationViewMode): string {
  return mode === "hidden" ? "No hay notificaciones ocultas" : "Sin notificaciones";
}

type BellVariant = "header" | "sidebar" | "mobile";

interface NotificationBellProps {
  /** Controla el anclaje visual del panel — el comportamiento es idéntico en los tres. */
  variant?: BellVariant;
}

function BellIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

interface NotificationRowProps {
  notification: NotificationDTO;
  /** Determina la acción de la fila: "Ocultar" en la bandeja normal, "Restaurar" en "Ocultas". */
  mode: NotificationViewMode;
  onOpen: (n: NotificationDTO) => void;
  onHide: (n: NotificationDTO) => void;
  onUnhide: (n: NotificationDTO) => void;
}

function NotificationRow({ notification, mode, onOpen, onHide, onUnhide }: NotificationRowProps) {
  const unread = notification.readAt === null;
  const isHiddenView = mode === "hidden";
  return (
    <li className={`notif-item${unread ? " notif-item--unread" : ""}`}>
      <button type="button" className="notif-item-body" onClick={() => onOpen(notification)}>
        <span className="notif-item-dot" aria-hidden="true" />
        <span className="notif-item-text">
          <span className="notif-item-title">{notification.title}</span>
          {notification.body && <span className="notif-item-desc">{notification.body}</span>}
          <span className="notif-item-time">{formatRelativeTime(notification.createdAt)}</span>
        </span>
      </button>
      <button
        type="button"
        className="notif-item-hide"
        onClick={() => (isHiddenView ? onUnhide(notification) : onHide(notification))}
        aria-label={
          isHiddenView
            ? `Restaurar notificación: ${notification.title}`
            : `Ocultar notificación: ${notification.title}`
        }
        title={isHiddenView ? "Restaurar" : "Ocultar"}
      >
        {isHiddenView ? "↺" : "✕"}
      </button>
    </li>
  );
}

export function NotificationBell({ variant = "header" }: NotificationBellProps) {
  const {
    notifications,
    mode,
    setMode,
    unreadCount,
    connectionPhase,
    lastSyncAt,
    loading,
    error,
    markRead,
    hide,
    unhide,
  } = useNotificationCenter();

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Click afuera / Escape cierran el panel — mismo patrón que
  // UserFooterPopover / CompanySelector de AdminShell.tsx.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const handleOpenNotification = (n: NotificationDTO) => {
    if (n.readAt === null) void markRead(n.id);
    const target = resolveNotificationTarget(n);
    if (target) {
      setOpen(false);
      navigate(target);
    }
  };

  const handleHide = (n: NotificationDTO) => {
    void hide(n.id)
      .then(() => {
        toast("Notificación ocultada", {
          action: { label: "Deshacer", onClick: () => void unhide(n.id) },
        });
      })
      .catch(() => {
        toast.error("No se pudo ocultar la notificación");
      });
  };

  const handleUnhide = (n: NotificationDTO) => {
    void unhide(n.id).catch(() => {
      toast.error("No se pudo restaurar la notificación");
    });
  };

  const toggleMode = () => setMode(mode === "hidden" ? "visible" : "hidden");

  return (
    <div className={`notif-bell notif-bell--${variant}`} ref={wrapRef}>
      <button
        type="button"
        className="notif-bell-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={describeBellAriaLabel(unreadCount)}
        data-tooltip={variant === "sidebar" ? "Notificaciones" : undefined}
      >
        <BellIcon />
        {variant === "sidebar" && <span className="notif-bell-label">Notificaciones</span>}
        {unreadCount > 0 && (
          <span className="notif-bell-badge" aria-hidden="true">
            {formatUnreadBadge(unreadCount)}
          </span>
        )}
      </button>

      {open && (
        <section className="notif-panel" aria-label="Notificaciones">
          <div className="notif-panel-header">
            <span className="notif-panel-title">Notificaciones</span>
            <span
              className={`notif-panel-status${
                connectionPhase === "open" ? " notif-panel-status--live" : ""
              }`}
            >
              <span className="notif-panel-status-dot" aria-hidden="true" />
              {describeConnectionStatus(connectionPhase, lastSyncAt)}
            </span>
          </div>

          <div className="notif-panel-toolbar">
            <button
              type="button"
              className={`notif-panel-toggle${mode === "hidden" ? " notif-panel-toggle--active" : ""}`}
              aria-pressed={mode === "hidden"}
              onClick={toggleMode}
            >
              {describeToggleLabel(mode)}
            </button>
          </div>

          {error && (
            <div className="notif-panel-error" role="alert">
              {error}
            </div>
          )}

          {loading && notifications.length === 0 && (
            <div className="notif-panel-loading">
              <div className="spinner" />
            </div>
          )}

          {!loading && notifications.length === 0 && !error && (
            <div className="notif-panel-empty">{describeEmptyState(mode)}</div>
          )}

          {notifications.length > 0 && (
            <ul className="notif-list">
              {notifications.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  mode={mode}
                  onOpen={handleOpenNotification}
                  onHide={handleHide}
                  onUnhide={handleUnhide}
                />
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
